import { useState, useEffect, useMemo } from "react";

export const useChartConfig = (chartData, yAxisConfig, darkMode = false) => {
  const [chartType, setChartType] = useState("line");
  const [showGrid, setShowGrid] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [animationEnabled, setAnimationEnabled] = useState(true);
  const [zoomType, setZoomType] = useState("x");

  // Generate CanvasJS configuration
  const canvasConfig = useMemo(() => {
    if (!chartData.length) return null;

    return {
      animationEnabled: animationEnabled,
      zoomEnabled: true,
      zoomType: zoomType,
      theme: darkMode ? "dark2" : "light2",
      title: {
        text: "",
        fontSize: 16,
      },
      legend: {
        cursor: "pointer",
        fontSize: 12,
        itemclick: function (e) {
          if (
            typeof e.dataSeries.visible === "undefined" ||
            e.dataSeries.visible
          ) {
            e.dataSeries.visible = false;
          } else {
            e.dataSeries.visible = true;
          }
          e.chart.render();
        },
      },
      toolTip: {
        shared: true,
        contentFormatter: function (e) {
          let content = `<strong>${new Date(e.entries[0].dataPoint.x).toLocaleString()}</strong><br/>`;
          e.entries.forEach((entry) => {
            const series = chartData.find(
              (s) => s.name === entry.dataSeries.name,
            );
            const isCompVib = series?.key.toLowerCase().includes("comp_vib");
            content += `<span style="color: ${entry.dataSeries.color}">${entry.dataSeries.name}</span>: `;

            if (isCompVib) {
              // For comp_vib, show ON/OFF based on the value
              const status = entry.dataPoint.y === 1 ? "ON" : "OFF";
              content += `<strong>${entry.dataPoint.y} (${status})</strong><br/>`;
            } else {
              content += `<strong>${entry.dataPoint.y.toFixed(2)} ${series?.unit || ""}</strong><br/>`;
            }
          });
          return content;
        },
      },
      axisX: {
        title: "Time",
        valueFormatString: "MMM DD, YY @ HH:mm",
        gridThickness: showGrid ? 1 : 0,
        tickLength: 5,
        lineThickness: 1,
      },
      axisY:
        yAxisConfig.length > 0
          ? yAxisConfig.map((axis, index) => ({
              title: axis.label,
              titleFontSize: 12,
              gridThickness: showGrid && index === 0 ? 1 : 0,
              lineThickness: 1,
              tickLength: 5,
              includeZero: false,
              suffix: axis.unit ? ` ${axis.unit}` : "",
              ...(index > 0 && { opposite: true }),
            }))
          : [
              {
                title: "Value",
                gridThickness: showGrid ? 1 : 0,
                includeZero: false,
              },
            ],
      data: chartData.map((series) => {
        const isCompVib = series.key.toLowerCase().includes("comp_vib");

        return {
          type: chartType,
          name: series.name,
          showInLegend: showLegend,
          color: series.color,
          lineThickness: isCompVib ? 3 : 2,
          markerSize: isCompVib ? 6 : 4,
          markerType: isCompVib ? "square" : "circle",
          yValueFormatString: series.unit
            ? `#,##0.## ${series.unit}`
            : "#,##0.##",
          xValueFormatString: "MMM DD, YY @ HH:mm:ss",
          dataPoints: series.data,
          ...(yAxisConfig.length > 1 && {
            axisYIndex:
              yAxisConfig.findIndex((axis) => axis.id === series.yAxisID) || 0,
          }),
        };
      }),
    };
  }, [
    chartData,
    yAxisConfig,
    chartType,
    showGrid,
    showLegend,
    animationEnabled,
    zoomType,
    darkMode,
  ]);

  // Generate FusionCharts configuration
  const fusionConfig = useMemo(() => {
    if (!chartData.length) return null;

    // Create schema for time series
    const schema = [
      {
        name: "Time",
        type: "date",
        format: "%Y-%m-%d %H:%M:%S",
      },
      ...chartData.map((series) => ({
        name: series.name,
        type: "number",
      })),
    ];

    // Prepare data in FusionCharts format
    const fusionData = [];

    // Get all unique timestamps
    const allTimestamps = new Set();
    chartData.forEach((series) => {
      series.data.forEach((point) => {
        allTimestamps.add(point.x.getTime());
      });
    });

    // Sort timestamps and create data rows
    const sortedTimestamps = Array.from(allTimestamps).sort();

    sortedTimestamps.forEach((timestamp) => {
      const row = [
        new Date(timestamp).toISOString().slice(0, 19).replace("T", " "),
      ];

      chartData.forEach((series) => {
        const dataPoint = series.data.find(
          (point) => point.x.getTime() === timestamp,
        );
        row.push(dataPoint ? dataPoint.y : null);
      });

      fusionData.push(row);
    });

    return {
      type: "timeseries",
      width: "100%",
      height: "100%",
      dataSource: {
        chart: {
          theme: darkMode ? "candy" : "fusion",
          exportEnabled: "1",
        },
        caption: {
          text: "",
        },
        data: {
          schema: schema,
          data: fusionData,
        },
        yAxis: yAxisConfig.map((axis, index) => ({
          plot: axis.series.map((seriesKey) => {
            const series = chartData.find((s) => s.key === seriesKey);
            return {
              value: series?.name || seriesKey,
              type: "line",
              style: {
                plot: {
                  "stroke-width": "2",
                },
              },
            };
          }),
          title: axis.label,
          ...(index > 0 && { opposite: "1" }),
        })),
      },
    };
  }, [chartData, yAxisConfig, darkMode]);

  // Chart configuration controls
  const configControls = {
    chartType: {
      value: chartType,
      setValue: setChartType,
      options: [
        { value: "line", label: "Line Chart" },
        { value: "spline", label: "Smooth Line" },
        { value: "area", label: "Area Chart" },
        { value: "column", label: "Column Chart" },
        { value: "scatter", label: "Scatter Plot" },
      ],
    },
    showGrid: {
      value: showGrid,
      setValue: setShowGrid,
    },
    showLegend: {
      value: showLegend,
      setValue: setShowLegend,
    },
    animationEnabled: {
      value: animationEnabled,
      setValue: setAnimationEnabled,
    },
    zoomType: {
      value: zoomType,
      setValue: setZoomType,
      options: [
        { value: "x", label: "Horizontal Zoom" },
        { value: "y", label: "Vertical Zoom" },
        { value: "xy", label: "Both Directions" },
      ],
    },
  };

  return {
    canvasConfig,
    fusionConfig,
    configControls,
    chartType,
    showGrid,
    showLegend,
    animationEnabled,
    zoomType,
  };
};
